import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner, Alert, Table, Badge, ProgressBar, Tabs, Tab } from "react-bootstrap";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, formatPercent, formatDate } from "../utils/formatters";
import './VentesJour.css';

export default function VentesJour() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState('factures');
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchVentesJour();
    
    // Auto-refresh toutes les 60 secondes si activé
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchVentesJour, 60000);
    }
    return () => clearInterval(interval);
  }, [selectedDate, autoRefresh]);

  const fetchVentesJour = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(
        `https://sawd.julesferry-ga.com/api/ventes_jour.php?date=${selectedDate}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      ); 
      
      if (!response.ok) throw new Error("Erreur de chargement des données");
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleRefresh = () => {
    fetchVentesJour();
  };

  const getStatusBadge = (statut) => {
    const badges = {
      'emise': { bg: 'warning', text: 'Émise' },
      'payee': { bg: 'success', text: 'Payée' },
      'payee_partielle': { bg: 'info', text: 'Partielle' },
      'impayee': { bg: 'danger', text: 'Impayée' },
      'annulee': { bg: 'secondary', text: 'Annulée' }
    };
    const badge = badges[statut] || { bg: 'secondary', text: statut };
    return <Badge bg={badge.bg}>{badge.text}</Badge>;
  };

  const getModePaiementIcon = (mode) => {
    const icons = {
      'especes': 'ri-money-cny-circle-line',
      'cheque': 'ri-bank-line',
      'virement': 'ri-bank-card-line',
      'carte_bancaire': 'ri-bank-card-2-line',
      'traite': 'ri-file-copy-line'
    };
    return icons[mode] || 'ri-bank-line';
  };

  const getModePaiementLabel = (mode) => {
    const labels = {
      'especes': 'Espèces',
      'cheque': 'Chèque',
      'virement': 'Virement',
      'carte_bancaire': 'Carte bancaire',
      'traite': 'Traite'
    };
    return labels[mode] || mode;
  };

  // Configuration des graphiques
  const seriesVentesHeure = [{
    name: 'Montant des ventes',
    data: data?.ventes_par_heure?.map(item => item.montant_total) || []
  }];

  const optionsVentesHeure = {
    chart: {
      type: 'area',
      height: 350,
      toolbar: { show: true },
      zoom: { enabled: true }
    },
    colors: ['#4361ee'],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.2
      }
    },
    xaxis: {
      categories: data?.ventes_par_heure?.map(item => `${item.heure}h`) || [],
      title: { text: 'Heure' }
    },
    yaxis: {
      title: { text: 'Montant (FCFA)' },
      labels: { formatter: (value) => formatCurrency(value, 'XOF') }
    },
    tooltip: {
      y: { formatter: (value) => formatCurrency(value) }
    },
    dataLabels: { enabled: false }
  };

  const seriesRepartitionPaiements = data?.moyens_paiement?.map(item => item.total) || [];

  const optionsRepartitionPaiements = {
    chart: {
      type: 'donut',
      height: 300
    },
    labels: data?.moyens_paiement?.map(item => getModePaiementLabel(item.mode_reglement)) || [],
    colors: ['#4361ee', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'],
    legend: { position: 'bottom' },
    tooltip: {
      y: { formatter: (value) => formatCurrency(value) }
    },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              formatter: () => formatCurrency(data?.moyens_paiement?.reduce((acc, item) => acc + item.total, 0) || 0)
            }
          }
        }
      }
    }
  };

  if (loading && !data) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Chargement des ventes du jour...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="danger">
          <Alert.Heading>Erreur</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </div>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const isToday = selectedDate === today;

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* En-tête */}
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
              <li className="breadcrumb-item active" aria-current="page">Ventes du jour</li>
            </ol>
            <h4 className="main-title mb-0">
              {isToday ? "Ventes d'aujourd'hui" : `Ventes du ${formatDate(selectedDate)}`}
            </h4>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <div className="d-flex align-items-center me-2">
              <input
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                className="form-control form-control-sm"
                max={today}
              />
            </div>
            <div className="form-check form-switch me-3 d-flex align-items-center">
              <input
                className="form-check-input me-2"
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                id="autoRefresh"
              />
              <label className="form-check-label small" htmlFor="autoRefresh">
                Auto-refresh
              </label>
            </div>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              className="d-flex align-items-center gap-1"
            >
              <i className={`ri-refresh-line ${loading ? 'fa-spin' : ''}`}></i>
              Actualiser
            </Button>
            {/* <Button variant="outline-success" size="sm" className="d-flex align-items-center gap-1">
              <i className="ri-printer-line"></i>
              Ticket de caisse
            </Button> */}
          </div>
        </div>

        {/* KPI Cards */}
        <Row className="g-3 mb-4">
          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-primary-soft rounded-circle p-3 me-3">
                    <i className="ri-shopping-bag-3-line fs-24 text-primary"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">Chiffre d'affaires</h6>
                    <h5 className="mb-0 fw-bold text-primary">{formatCurrency(data?.resume?.total_ttc || 0)}</h5>
                    <small className={`${data?.comparaison?.evolution >= 0 ? 'text-success' : 'text-danger'}`}>
                      <i className={`ri-arrow-${data?.comparaison?.evolution >= 0 ? 'up' : 'down'}-line`}></i>
                      {Math.abs(data?.comparaison?.evolution || 0)}% vs hier
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-success-soft rounded-circle p-3 me-3">
                    <i className="ri-file-list-3-line fs-24 text-success"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">Factures</h6>
                    <h5 className="mb-0 fw-bold">{data?.resume?.nb_factures || 0}</h5>
                    <small className={`${data?.comparaison?.evolution_factures >= 0 ? 'text-success' : 'text-danger'}`}>
                      <i className={`ri-arrow-${data?.comparaison?.evolution_factures >= 0 ? 'up' : 'down'}-line`}></i>
                      {Math.abs(data?.comparaison?.evolution_factures || 0)}% vs hier
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-warning-soft rounded-circle p-3 me-3">
                    <i className="ri-group-line fs-24 text-warning"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">Clients servis</h6>
                    <h5 className="mb-0 fw-bold">{data?.resume?.nb_clients || 0}</h5>
                    <small className="text-secondary">Panier moyen: {formatCurrency(data?.resume?.nb_factures > 0 ? data?.resume?.total_ttc / data?.resume?.nb_factures : 0)}</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-info-soft rounded-circle p-3 me-3">
                    <i className="ri-stack-line fs-24 text-info"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">Produits vendus</h6>
                    <h5 className="mb-0 fw-bold">{data?.resume?.quantite_totale || 0}</h5>
                    <small className="text-secondary">{data?.resume?.nb_produits || 0} références</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Graphiques et stats */}
        <Row className="g-3 mb-4">
          <Col xl="8">
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0 d-flex align-items-center">
                <Card.Title as="h6" className="mb-0">Ventes par heure</Card.Title>
                <div className="ms-auto">
                  <Badge bg="light" text="dark" className="me-2">
                    Total: {formatCurrency(data?.ventes_par_heure?.reduce((acc, item) => acc + item.montant_total, 0) || 0)}
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body>
                <ReactApexChart 
                  series={seriesVentesHeure} 
                  options={optionsVentesHeure} 
                  type="area" 
                  height={350} 
                />
              </Card.Body>
            </Card>
          </Col>

          <Col xl="4">
            <Card className="border-0 shadow-sm h-100">
              <Card.Header className="bg-transparent border-0">
                <Card.Title as="h6" className="mb-0">Répartition des paiements</Card.Title>
              </Card.Header>
              <Card.Body>
                {data?.moyens_paiement?.length > 0 ? (
                  <>
                    <ReactApexChart 
                      series={seriesRepartitionPaiements} 
                      options={optionsRepartitionPaiements} 
                      type="donut" 
                      height={250} 
                    />
                    <div className="mt-3">
                      {data?.moyens_paiement?.map((paiement, index) => (
                        <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center">
                            <i className={`${getModePaiementIcon(paiement.mode_reglement)} me-2 fs-18`}></i>
                            <span className="small">{getModePaiementLabel(paiement.mode_reglement)}</span>
                          </div>
                          <div>
                            <span className="fw-bold me-2">{formatCurrency(paiement.total)}</span>
                            <Badge bg="light" text="dark">{paiement.nb_paiements}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-5">
                    <i className="ri-bank-card-line fs-48 text-secondary mb-3"></i>
                    <p>Aucun paiement enregistré</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Onglets des détails */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Header className="bg-transparent border-0">
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-3"
            >
              <Tab eventKey="factures" title={
                <span><i className="ri-file-list-3-line me-2"></i>Factures ({data?.factures?.length || 0})</span>
              }>
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>N° Facture</th>
                        <th>Heure</th>
                        <th>Client</th>
                        <th>Montant HT</th>
                        <th>TVA</th>
                        <th>TTC</th>
                        <th>Réglé</th>
                        <th>Reste</th>
                        <th>Statut</th>
                        <th>Vendeur</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.factures?.length > 0 ? (
                        data.factures.map((facture) => (
                          <tr key={facture.id_facture}>
                            <td className="fw-semibold">
                              <Link to={`/ventes/factures/${facture.id_facture}`} className="text-decoration-none">
                                {facture.numero_facture}
                              </Link>
                            </td>
                            <td>{new Date(facture.date_facture).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</td>
                            <td>
                              <div>
                                <span className="d-block fw-semibold">{facture.raison_sociale}</span>
                                <small className="text-secondary">{facture.code_client}</small>
                              </div>
                            </td>
                            <td className="text-end">{formatCurrency(facture.montant_ht)}</td>
                            <td className="text-end text-warning">{formatCurrency(facture.montant_tva)}</td>
                            <td className="text-end fw-bold">{formatCurrency(facture.montant_ttc)}</td>
                            <td className="text-end text-success">{formatCurrency(facture.montant_regle)}</td>
                            <td className="text-end text-danger">{formatCurrency(facture.montant_ttc - facture.montant_regle)}</td>
                            <td>{getStatusBadge(facture.statut)}</td>
                            <td>
                              <small>
                                {facture.prenom_utilisateur} {facture.nom_utilisateur}
                              </small>
                            </td>
                            <td>
                              <Button variant="link" size="sm" className="p-0 text-primary">
                                <i className="ri-printer-line"></i>
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="11" className="text-center py-4">
                            <i className="ri-inbox-line fs-48 text-secondary d-block mb-3"></i>
                            <p className="text-secondary mb-0">Aucune facture pour cette date</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot className="table-light fw-bold">
                      <tr>
                        <td colSpan="5" className="text-end">TOTAUX:</td>
                        <td className="text-end">{formatCurrency(data?.resume?.total_ttc || 0)}</td>
                        <td className="text-end">{formatCurrency(data?.resume?.total_regle || 0)}</td>
                        <td className="text-end">{formatCurrency(data?.resume?.reste_a_payer || 0)}</td>
                        <td colSpan="3"></td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              </Tab>

              <Tab eventKey="produits" title={
                <span><i className="ri-suitcase-2-line me-2"></i>Produits vendus ({data?.produits?.length || 0})</span>
              }>
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Référence</th>
                        <th>Produit</th>
                        <th>Catégorie</th>
                        <th className="text-end">Quantité</th>
                        <th className="text-end">Prix unitaire</th>
                        <th className="text-end">Total HT</th>
                        <th className="text-end">% des ventes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.produits?.length > 0 ? (
                        data.produits.map((produit) => {
                          const pourcentage = (produit.total_ligne_ht / (data?.resume?.total_ht || 1)) * 100;
                          return (
                            <tr key={produit.id_produit}>
                              <td><Badge bg="light" text="dark">{produit.reference}</Badge></td>
                              <td className="fw-semibold">{produit.designation}</td>
                              <td>{produit.nom_categorie || '-'}</td>
                              <td className="text-end fw-bold">{formatNumber(produit.quantite_vendue)}</td>
                              <td className="text-end">{formatCurrency(produit.prix_unitaire_ht)}</td>
                              <td className="text-end text-primary">{formatCurrency(produit.total_ligne_ht)}</td>
                              <td className="text-end">
                                <div className="d-flex align-items-center gap-2">
                                  <ProgressBar now={pourcentage} style={{ width: '80px', height: '6px' }} />
                                  <small className="text-secondary">{pourcentage.toFixed(1)}%</small>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <i className="ri-inbox-line fs-48 text-secondary d-block mb-3"></i>
                            <p className="text-secondary mb-0">Aucun produit vendu</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Tab>

              <Tab eventKey="paiements" title={
                <span><i className="ri-bank-card-line me-2"></i>Paiements ({data?.paiements?.length || 0})</span>
              }>
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>N° Opération</th>
                        <th>Heure</th>
                        <th>Client</th>
                        <th>Facture</th>
                        <th>Mode</th>
                        <th>Référence</th>
                        <th className="text-end">Montant</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.paiements?.length > 0 ? (
                        data.paiements.map((paiement) => (
                          <tr key={paiement.id_reglement}>
                            <td className="fw-semibold">{paiement.numero_reglement}</td>
                            <td>{new Date(paiement.date_reglement).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</td>
                            <td>{paiement.client}</td>
                            <td>{paiement.numero_facture}</td>
                            <td>
                              <i className={`${getModePaiementIcon(paiement.mode_reglement)} me-2`}></i>
                              {getModePaiementLabel(paiement.mode_reglement)}
                            </td>
                            <td><small>{paiement.reference || '-'}</small></td>
                            <td className="text-end fw-bold text-success">{formatCurrency(paiement.montant)}</td>
                            <td>
                              <Badge bg={paiement.statut === 'valide' ? 'success' : 'warning'}>
                                {paiement.statut === 'valide' ? 'Validé' : 'En attente'}
                              </Badge>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center py-4">
                            <i className="ri-inbox-line fs-48 text-secondary d-block mb-3"></i>
                            <p className="text-secondary mb-0">Aucun paiement enregistré</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot className="table-light fw-bold">
                      <tr>
                        <td colSpan="6" className="text-end">TOTAL:</td>
                        <td className="text-end text-success">
                          {formatCurrency(data?.paiements?.reduce((acc, p) => acc + p.montant, 0) || 0)}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              </Tab>

              <Tab eventKey="vendeurs" title={
                <span><i className="ri-user-star-line me-2"></i>Performance vendeurs</span>
              }>
                <Row>
                  {data?.top_vendeurs?.length > 0 ? (
                    data.top_vendeurs.map((vendeur, index) => {
                      const pourcentage = (vendeur.total_ventes / (data?.resume?.total_ttc || 1)) * 100;
                      return (
                        <Col md="4" key={vendeur.id_utilisateur} className="mb-3">
                          <Card className="border-0 shadow-sm">
                            <Card.Body>
                              <div className="d-flex align-items-center mb-3">
                                <div className={`rank-badge rank-${index + 1} me-3`}>
                                  #{index + 1}
                                </div>
                                <div>
                                  <h6 className="mb-1">
                                    {vendeur.prenom_utilisateur} {vendeur.nom_utilisateur}
                                  </h6>
                                  <small className="text-secondary">{vendeur.nb_ventes} ventes</small>
                                </div>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span>CA réalisé</span>
                                <span className="fw-bold text-primary">{formatCurrency(vendeur.total_ventes)}</span>
                              </div>
                              <ProgressBar now={pourcentage} variant="success" />
                              <small className="text-secondary d-block text-end mt-1">
                                {pourcentage.toFixed(1)}% du total
                              </small>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })
                  ) : (
                    <Col>
                      <p className="text-center py-4 text-secondary">Aucune donnée vendeur</p>
                    </Col>
                  )}
                </Row>
              </Tab>
            </Tabs>
          </Card.Header>
        </Card>

        <Footer />
      </div>
    </React.Fragment>
  );
}